import React, { useState, useEffect } from 'react';
import {
  params,
  extend,
  string as stringType,
  useLazyDataModel,
  shape as shapeType,
  arrayOf
} from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import {
  FormController,
  FormLabel,
  Dropdown,
  MenuItem,
  TextField
} from '@one-thd/sui-atomic-components';
import {
  validateField
} from '../helpers';
import {
  EMPTY_ZIP_CODE_MESSAGE,
  EMPTY_ADDRESS_MESSAGE,
  INVALID_ZIP_CODE,
  ADDRESS_LINE_1_KEY
} from '../constants';

const { bool, func, string, shape } = proptypes;

export const JobsiteInformationForm = ({
  showErrors,
  jobsiteInformation,
  setJobsiteInformation,
  isJobsiteError,
  setIsJobsiteError,
  open,
  isZipOnlyEnabled
}) => {

  const [errors, setErrors] = useState({
    addressLine1Error: '',
    addressLine2Error: '',
    zipCodeError: ''
  });

  const {
    addressLine1Error,
    addressLine2Error,
    zipCodeError
  } = errors;

  // Safely access jobsite information with default values
  const {
    addressLine1 = '',
    addressLine2 = '',
    zipCode = '',
    city = '',
    state = ''
  } = jobsiteInformation || {};

  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [dropdownSelection, setDropdownSelection] = useState('');
  const [placeholderValue, setPlaceholderValue] = useState('');
  const handleDropdownSelection = (event) => {
    setDropdownSelection(event.target.value);
  };

  const [
    fetchCityState, { data: cityAndStateData, error: cityAndStateError }
  ] = useLazyDataModel('cityStateLookupByZipCode', {
    variables: {},
    skip: (zipCode?.length !== 5),
    context: { withAuth: true },
    fetchPolicy: 'cache-first'
  });

  useEffect(() => {
    if (zipCode?.length === 5) {
      fetchCityState({
        variables: {
          zipCode
        }
      });
    }
  }, [zipCode]);

  const getDropdownValue = () => {
    const sanitizedState = state?.trim();
    const cityAndState = `${city}, ${sanitizedState}`;
    const index = dropdownOptions.indexOf(cityAndState);
    const value = dropdownOptions[index];
    if (dropdownSelection?.length && index >= 0) {
      if (dropdownSelection !== placeholderValue) {
        setPlaceholderValue(dropdownSelection);
      }
      return dropdownSelection;
    }
    if (city && sanitizedState && dropdownOptions.length > 0) {
      if (value !== placeholderValue) {
        setPlaceholderValue(value);
      }
      return value;
    }
    if (value?.length && placeholderValue !== value) {
      setPlaceholderValue(value);
    } else if (placeholderValue !== dropdownOptions[0]) {
      setPlaceholderValue(dropdownOptions[0]);
    }
    return dropdownOptions[0] || '';

  };

  useEffect(() => {
    const cityAndStateArray = cityAndStateData?.cityStateLookupByZipCode?.postalDetailsList;
    if (cityAndStateArray) {
      setDropdownOptions([]);
      cityAndStateArray.forEach((location) => {
        setDropdownOptions((prevState) => ([
          ...prevState,
          `${location.city}, ${location.state}`
        ]));
      });
      setErrors((prevState) => ({
        ...prevState,
        zipCodeError: ''
      }));
    }
    if (cityAndStateError?.message) {
      setErrors((prevState) => ({
        ...prevState,
        zipCodeError: INVALID_ZIP_CODE
      }));
    }
  }, [cityAndStateData]);

  useEffect(() => {
    const sanitizedState = state?.trim();
    const cityAndState = `${city}, ${sanitizedState}`;
    const index = dropdownOptions?.indexOf(cityAndState);
    if (index < 0) {
      setPlaceholderValue(dropdownOptions[0] || '');
      setDropdownSelection(dropdownOptions[0] || '');
    } else {
      setPlaceholderValue(dropdownOptions[index]);
    }
    getDropdownValue();
  }, [dropdownOptions]);

  useEffect(() => {
    if (dropdownSelection?.length) {
      const cityAndStateMutation = dropdownSelection.split(',');
      const newCity = cityAndStateMutation[0];
      const newState = cityAndStateMutation[1];
      setJobsiteInformation((prevState) => ({
        ...prevState,
        city: newCity,
        state: newState
      }));
    }
  }, [dropdownSelection]);

  const handleAddressOneValidation = (value) => {
    const { isError, errorMessage } = validateField({ value, key: ADDRESS_LINE_1_KEY });
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: errorMessage
      }));
    } else if (!isError && addressLine1Error !== EMPTY_ADDRESS_MESSAGE) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: ''
      }));
    }
  };

  const handleAddressTwoValidation = (value) => {
    const { isError, errorMessage } = validateField({ value, key: ADDRESS_LINE_1_KEY });
    if (isError) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine2Error: errorMessage
      }));
    } else {
      setErrors((prevState) => ({
        ...prevState,
        addressLine2Error: ''
      }));
    }
  };

  const handleOnChange = (key, event) => {
    const { value } = event.target;
    setJobsiteInformation((prevState) => ({
      ...prevState,
      [key]: value
    }));
  };

  useEffect(() => {
    const errorMessageValues = Object.values(errors);
    const hasErrors = errorMessageValues.some(Boolean);
    if (hasErrors) {
      setIsJobsiteError(true);
    } else {
      setIsJobsiteError(false);
    }
  }, [errors]);

  const [isOptional, setIsOptional] = useState(true);
  useEffect(() => {
    if (!isZipOnlyEnabled) {
      // Pre-DSCVR-4956: touching any one of the three fields makes both the address line and the
      // zip required, so a zip on its own is an incomplete entry and blocks submit.
      const hasAnyField = Boolean(addressLine1 || addressLine2 || zipCode);
      setIsOptional(!hasAnyField);
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: (hasAnyField && !addressLine1) ? EMPTY_ADDRESS_MESSAGE : '',
        zipCodeError: (hasAnyField && !zipCode) ? EMPTY_ZIP_CODE_MESSAGE : ''
      }));
      return;
    }
    // A zip code on its own is a complete jobsite entry — it is enough to drive inventory and
    // delivery estimates without a street address. So the address pair only becomes required
    // once the customer starts entering an address line: an address line with no zip is still
    // an error, but a zip with no address line is not.
    const hasAddressLine = Boolean(addressLine1 || addressLine2);
    setIsOptional(!hasAddressLine);
    setErrors((prevState) => ({
      ...prevState,
      addressLine1Error: (hasAddressLine && !addressLine1) ? EMPTY_ADDRESS_MESSAGE : '',
      zipCodeError: (hasAddressLine && !zipCode) ? EMPTY_ZIP_CODE_MESSAGE : ''
    }));
  }, [addressLine1, addressLine2, zipCode, isZipOnlyEnabled]);

  useEffect(() => {
    if (!open && isJobsiteError) {
      setErrors((prevState) => ({
        ...prevState,
        addressLine1Error: '',
        addressLine2Error: '',
        cityError: '',
        stateError: '',
        zipCodeError: ''
      }));
    }
  }, [open]);

  return (
    <div className="sui-grid sui-gap-6">
      <TextField
        fullWidth
        optional={isOptional}
        value={addressLine1 || ''}
        id="jobsite-address-one"
        label="Address Line 1"
        name="addressLine1"
        onBlur={() => { handleAddressOneValidation(addressLine1); }}
        onChange={(event) => { handleOnChange('addressLine1', event); }}
        status={
          showErrors && addressLine1Error ? 'error' : null
        }
        statusMessage={
          showErrors ? addressLine1Error : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'jobsite-address-one-input'
          }
        }}
      />
      <TextField
        fullWidth
        optional
        value={addressLine2 || ''}
        id="jobsite-address-two"
        label="Address Line 2"
        name="addressLine2"
        onBlur={() => { handleAddressTwoValidation(addressLine2); }}
        onChange={(event) => { handleOnChange('addressLine2', event); }}
        status={
          showErrors && addressLine2Error ? 'error' : null
        }
        statusMessage={
          showErrors ? addressLine2Error : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'jobsite-address-two-input'
          }
        }}
      />
      <TextField
        fullWidth
        optional={isOptional}
        value={zipCode || ''}
        id="jobsite-zipcode"
        label="Zip Code"
        name="zipCode"
        onChange={(event) => { handleOnChange('zipCode', event); }}
        status={
          showErrors && zipCodeError ? 'error' : null
        }
        statusMessage={
          showErrors ? zipCodeError : ''
        }
        InputProps={{
          inputProps: {
            'data-testid': 'jobsite-zipcode-input',
            maxLength: '5'
          }
        }}
      />
      <FormController fullWidth>
        <FormLabel
          optional
          htmlFor="jobsite-information-dropdown"
          id="jobsite-information-dropdown-label"
        >
          City, State
        </FormLabel>
        <Dropdown
          id="jobsite-information-dropdown"
          disabled={!dropdownOptions[0]}
          value={getDropdownValue()}
          onChange={(event) => { handleDropdownSelection(event); }}
          placeholder={placeholderValue || dropdownOptions[0] || ''}
        >
          {(dropdownOptions[0]) && (
            dropdownOptions.map((location, index) => (
              <MenuItem key={dropdownOptions[index] || ''} value={location}>{location}</MenuItem>
            ))
          )}
        </Dropdown>
      </FormController>
    </div>
  );
};

JobsiteInformationForm.displayName = 'JobsiteInformationForm';

JobsiteInformationForm.propTypes = {
  showErrors: bool.isRequired,
  jobsiteInformation: shape({
    addressLine1: string,
    addressLine2: string,
    city: string,
    state: string,
    zipCode: string,
    country: string
  }).isRequired,
  setJobsiteInformation: func.isRequired,
  isJobsiteError: bool.isRequired,
  setIsJobsiteError: func.isRequired,
  open: bool.isRequired,
  isZipOnlyEnabled: bool
};

JobsiteInformationForm.defaultProps = {
  // Defaults on so the other consumer of this form - ProjectDetailsForm - keeps the behavior it
  // has today. Only the create drawer passes this, driven by fs:isJobsiteInformationEnabled.
  isZipOnlyEnabled: true
};

JobsiteInformationForm.dataModel = extend({
  cityStateLookupByZipCode: params({
    zipCode: stringType().isRequired()
  }).shape({
    postalDetailsList: arrayOf(
      shapeType({
        city: stringType(),
        state: stringType()
      })
    )
  })
});